import type { OffAirReason } from '@shared/api.ts'
import {
  type Component,
  createEffect,
  createSignal,
  Match,
  on,
  onCleanup,
  onMount,
  Show,
  Switch,
} from 'solid-js'
import { CommentPanel } from '~/components/CommentPanel'
import { Layout } from '~/components/Layout'
import { LiveBar } from '~/components/LiveBar'
import { NextProgramNotice } from '~/components/NextProgramNotice'
import { NowPanel } from '~/components/NowPanel'
import { PanelTabs } from '~/components/PanelTabs'
import { PlayerFrame } from '~/components/PlayerFrame'
import { ProgramGuide } from '~/components/ProgramGuide'
import { ReactionOverlay } from '~/components/ReactionOverlay'
import { ReturnToLive } from '~/components/ReturnToLive'
import { StartOverlay } from '~/components/StartOverlay'
import { TheaterBar } from '~/components/TheaterBar'
import { type PlayerState, YouTubePlayer } from '~/components/YouTubePlayer'
import { reportPlaybackError } from '~/lib/api'
import { createBroadcastStore } from '~/lib/broadcast-store'
import { isOffAirPosition, playbackDrift } from '~/lib/clock'
import { createLiveStore } from '~/lib/live-store'
import { createTheaterStore } from '~/lib/theater'
import { playerErrorReason } from '~/lib/youtube'

/** 自動再生が弾かれたかどうかを判定するまでの待ち時間 */
const AUTOPLAY_CHECK_MS = 1500

/** 実際の再生位置を確認する間隔 */
const PLAYBACK_POLL_MS = 1000

/** 狭い画面でどのパネルを出しているか */
type Pane = 'comments' | 'schedule'

export const App: Component = () => {
  const broadcast = createBroadcastStore()
  const live = createLiveStore(
    () => broadcast.currentProgram()?.startAt,
    () => broadcast.currentProgram()?.videoId,
  )
  const theater = createTheaterStore()

  const [player, setPlayer] = createSignal<YT.Player>()
  const [playerState, setPlayerState] = createSignal<PlayerState>('unstarted')
  const [needsGesture, setNeedsGesture] = createSignal(false)
  /** ユーザーが明示的に一時停止しているか */
  const [paused, setPaused] = createSignal(false)
  /** 一時停止中に番組が切り替わったら、その番組の開始時刻を持つ */
  const [missedProgramStart, setMissedProgramStart] = createSignal<string | null>(null)
  /** Player 上の実際の再生位置（秒） */
  const [playbackSeconds, setPlaybackSeconds] = createSignal(0)
  /** 狭い画面でどちらのパネルを出しているか */
  const [pane, setPane] = createSignal<Pane>('comments')
  /** 全画面で実況を開いているか */
  const [theaterComments, setTheaterComments] = createSignal(false)

  let autoplayTimer: ReturnType<typeof setTimeout> | undefined
  onCleanup(() => clearTimeout(autoplayTimer))

  // 一時停止・巻き戻し・早送りで放送位置から外れていないかを見る（要件定義 3.4）
  onMount(() => {
    const timer = setInterval(() => {
      const instance = player()
      if (!instance) return
      setPlaybackSeconds(instance.getCurrentTime())
    }, PLAYBACK_POLL_MS)
    onCleanup(() => clearInterval(timer))
  })

  /** 放送位置と実際の再生位置のずれ（秒） */
  const drift = (): number => playbackDrift(broadcast.broadcastOffsetSeconds(), playbackSeconds())

  /** 放送位置から外れているか。再生が始まる前は判定しない */
  const offLive = (): boolean =>
    broadcast.onAir() && playerState() !== 'unstarted' && isOffAirPosition(drift())

  const handleReady = (instance: YT.Player): void => {
    setPlayer(instance)
    instance.playVideo()
    autoplayTimer = setTimeout(() => {
      if (instance.getPlayerState() !== YT.PlayerState.PLAYING) setNeedsGesture(true)
    }, AUTOPLAY_CHECK_MS)
  }

  const handleStateChange = (state: PlayerState): void => {
    setPlayerState(state)
    if (state === 'playing') {
      setNeedsGesture(false)
      setPaused(false)
      setMissedProgramStart(null)
    }
    // 834CH 側から pauseVideo は呼ばないので、一時停止はユーザー操作とみなす
    if (state === 'paused') setPaused(true)
  }

  // 一時停止中に番組が切り替わったら、自動では再生を再開せず案内を出す（要件定義 3.5）
  createEffect(
    on(
      () => broadcast.currentProgram()?.startAt,
      (startAt, previousStartAt) => {
        if (previousStartAt && startAt && startAt !== previousStartAt && paused()) {
          setMissedProgramStart(startAt)
        }
      },
    ),
  )

  /** 再生できない動画に当たったら次の放送可能な番組へ送る（要件定義 13 章） */
  const handleError = (code: number): void => {
    const program = broadcast.currentProgram()
    if (!program) return
    console.warn(`${program.videoId} を再生できません: ${playerErrorReason(code)}`)
    void reportPlaybackError(program.videoId, code)
    broadcast.markUnavailable(program.videoId)
  }

  /** 現在の放送位置へ戻して再生する */
  const returnToLive = (): void => {
    const instance = player()
    if (!instance) return
    const offset = broadcast.broadcastOffsetSeconds()
    instance.seekTo(offset, true)
    instance.playVideo()
    setPlaybackSeconds(offset)
    setMissedProgramStart(null)
    setNeedsGesture(false)
  }

  // 全画面を出入りしたら実況は閉じた状態から始める
  createEffect(on(theater.active, () => setTheaterComments(false)))

  // class と classList を混ぜると、class の差し替えで classList の指定が消えてしまう。
  // 表示・非表示は一本の文字列で決める
  const commentsClass = (): string =>
    theater.active()
      ? `${THEATER_COMMENTS} ${theaterComments() ? 'flex' : 'hidden'}`
      : `${NORMAL_COMMENTS} ${pane() === 'comments' ? 'flex' : 'hidden'}`

  /** 番組表。全画面では映像に高さを譲るので出さない */
  const scheduleClass = (): string =>
    theater.active() ? 'hidden' : `${NORMAL_SCHEDULE} ${pane() === 'schedule' ? 'flex' : 'hidden'}`

  onMount(() => {
    const handleKey = (event: KeyboardEvent): void => {
      // コメントを書いている最中のキーは拾わない
      if ((event.target as HTMLElement | null)?.closest('input, textarea')) return

      if (event.key === 'f' || event.key === 'F') {
        event.preventDefault()
        theater.toggle()
        return
      }
      // 全画面にできなかった環境でも Esc で戻れるようにする
      if (event.key === 'Escape' && theater.active() && !document.fullscreenElement) theater.exit()
    }
    window.addEventListener('keydown', handleKey)
    onCleanup(() => window.removeEventListener('keydown', handleKey))
  })

  return (
    <Layout
      live={broadcast.onAir() && playerState() === 'playing'}
      viewers={live.viewers()}
      viewersConnected={live.connected()}
      theater={theater.active()}
      onToggleTheater={theater.toggle}
    >
      <div class={theater.active() ? THEATER_SHELL : NORMAL_SHELL}>
        {/* プレイヤーと、いま放送中の番組 */}
        <div class={theater.active() ? THEATER_STAGE : NORMAL_STAGE}>
          <PlayerFrame theater={theater.active()}>
            <Switch fallback={<PlayerMessage text="放送を読み込んでいます…" />}>
              <Match when={broadcast.now.error}>
                <PlayerMessage text="放送情報を取得できませんでした" />
              </Match>
              <Match when={broadcast.currentProgram()}>
                {(program) => (
                  <>
                    <YouTubePlayer
                      videoId={program().videoId}
                      startSeconds={broadcast.broadcastOffsetSeconds()}
                      autoplay={!paused()}
                      onReady={handleReady}
                      onStateChange={handleStateChange}
                      onError={handleError}
                    />
                    <Show when={needsGesture()}>
                      <StartOverlay onStart={returnToLive} />
                    </Show>
                  </>
                )}
              </Match>
              <Match when={broadcast.now() && !broadcast.onAir()}>
                <PlayerMessage text={offAirText(broadcast.now()?.reason)} />
              </Match>
            </Switch>

            {/* リアクションの粒。下端は Player のコントロールのために空けている */}
            <ReactionOverlay bursts={live.bursts()} />
          </PlayerFrame>

          {/* 全画面では映像を押し出さないよう、案内を重ねて出す */}
          <div class={theater.active() ? THEATER_NOTICE : 'contents'}>
            <Show when={offLive() && !missedProgramStart()}>
              <ReturnToLive drift={drift()} onReturn={returnToLive} />
            </Show>

            <Show when={missedProgramStart() ? broadcast.currentProgram() : null}>
              {(program) => <NextProgramNotice title={program().title} onResume={returnToLive} />}
            </Show>
          </div>

          {/* 全画面では同じ内容を帯に出すので、NOW は畳む */}
          <Show when={!theater.active() && broadcast.currentProgram()}>
            {(program) => (
              <NowPanel
                program={program()}
                nowMs={broadcast.broadcastNowMs()}
                slot={broadcast.now()?.slot}
                next={broadcast.now()?.next[0]}
              >
                {/* リアクションはそれだけの帯を持たず、見出しの脇に並べる */}
                <LiveBar
                  reactions={live.reactions()}
                  onReact={live.sendReaction}
                  programTitle={program().title}
                />
              </NowPanel>
            )}
          </Show>
        </div>

        {/* 狭い画面は 1 画面に収めるため、実況と番組表をタブで切り替える */}
        <Show when={!theater.active()}>
          <PanelTabs
            class="lg:hidden"
            tabs={[
              { id: 'comments', label: '実況' },
              { id: 'schedule', label: '番組表' },
            ]}
            active={pane()}
            onSelect={setPane}
          />
        </Show>

        {/* 実況。広い画面では右カラムに置き、全高を使う。全画面では映像に重ねる */}
        <aside class={commentsClass()}>
          <CommentPanel
            comments={live.comments()}
            connected={live.connected()}
            name={live.name()}
            rejection={live.rejection()}
            muted={live.muted()}
            onPost={live.postComment}
          />
        </aside>

        {/*
          番組表。広い画面ではプレイヤーの下で、残りの高さをスクロールさせる。
          放送中の番組が先頭に来るようスクロールするので、その下がそのまま
          「次に来るもの」になる。NEXT を別の一覧に分けていない（要件定義 16 章）
        */}
        <div class={scheduleClass()} role="tabpanel" aria-label="番組表">
          <div class="flex min-h-0 flex-1 flex-col">
            <ProgramGuide
              nowMs={broadcast.broadcastNowMs()}
              currentStartAt={broadcast.currentProgram()?.startAt}
              reveal={pane()}
            />
          </div>
        </div>

        {/*
          全画面の帯。映像には重ねず、下に高さを取って置く。
          リアクションと実況は手が届くところに無いと押されないので、
          上ではなく下にまとめている
        */}
        <Show when={theater.active()}>
          <TheaterBar
            live={broadcast.onAir() && playerState() === 'playing'}
            viewers={live.viewers()}
            viewersConnected={live.connected()}
            program={broadcast.currentProgram() ?? undefined}
            slot={broadcast.now()?.slot}
            commentsOpen={theaterComments()}
            onToggleComments={() => setTheaterComments((open) => !open)}
            onExit={theater.exit}
          >
            <LiveBar
              variant="bar"
              reactions={live.reactions()}
              onReact={live.sendReaction}
              programTitle={broadcast.currentProgram()?.title}
            />
          </TheaterBar>
        </Show>
      </div>
    </Layout>
  )
}

/** 通常表示の外枠 */
const NORMAL_SHELL =
  'mx-auto flex h-full w-full min-w-0 max-w-6xl flex-col gap-2 px-4 pb-2 sm:gap-3 sm:px-6 lg:grid lg:grid-cols-[minmax(0,1fr)_21rem] lg:grid-rows-[auto_minmax(0,1fr)] lg:gap-x-6 lg:gap-y-3'

/** 全画面の外枠。余白を持たず、画面をすべて映像に使う */
const THEATER_SHELL = 'relative flex h-full w-full min-w-0 flex-col bg-black'

const NORMAL_STAGE = 'flex min-w-0 shrink-0 flex-col gap-2 sm:gap-3 lg:col-start-1 lg:row-start-1'
const THEATER_STAGE = 'relative flex min-h-0 min-w-0 flex-1 flex-col'

/** 全画面で「放送に戻る」などを重ねる位置。中身が無いときは高さを持たない */
const THEATER_NOTICE = 'absolute left-1/2 top-3 z-30 w-full max-w-sm -translate-x-1/2 px-4'

const NORMAL_COMMENTS =
  'min-h-0 min-w-0 flex-1 flex-col pt-2 sm:pt-3 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:flex lg:pt-0'
/** 全画面の実況。下の帯にぶつからないよう、その上へ置く */
const THEATER_COMMENTS =
  'absolute inset-x-0 bottom-14 z-30 h-1/2 min-h-0 min-w-0 flex-col bg-ch-bg/95 p-3 backdrop-blur sm:bottom-10 sm:left-auto sm:top-0 sm:h-auto sm:w-80 sm:border-l sm:border-ch-border'

const NORMAL_SCHEDULE =
  'min-h-0 min-w-0 flex-1 flex-col pt-2 sm:pt-3 lg:col-start-1 lg:row-start-2 lg:flex lg:pt-0'

/** 放送休止の理由を視聴者向けの言い方にする */
const offAirText = (reason: OffAirReason | undefined): string => {
  switch (reason) {
    case 'no_schedule':
      return '番組表を準備しています'
    case 'no_playable_video':
      return '再生できる番組が見つかりませんでした'
    default:
      return 'ただいま放送を休止しています'
  }
}

const PlayerMessage: Component<{ text: string }> = (props) => (
  <div class="flex size-full items-center justify-center px-6 text-center text-sm text-ch-dim">
    {props.text}
  </div>
)
